import React, { createContext, useContext, useMemo, useRef, useSyncExternalStore } from 'react';

/**
 * SRP B2: hovering a job card or list row highlights that job's dot in the bottom panel's salary
 * chart (SrpPanelSalarySection). A plain external store rather than React state: hover fires on
 * every pointermove-driven mouseenter across potentially dozens of cards, and only the salary
 * chart (usually off-screen, inside the "Tama haku" panel) needs to re-render on change, not every
 * card. Desktop pointer only, callers gate on `(pointer: fine)` themselves (see hit-card-b2's
 * hover handlers) so nothing fires on touch devices.
 */
function createHoverStore() {
  let hoveredId: string | null = null;
  const listeners = new Set<() => void>();
  return {
    setHovered(id: string | null) {
      if (hoveredId === id) return;
      hoveredId = id;
      listeners.forEach((l) => l());
    },
    getHovered() {
      return hoveredId;
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

type HoverStore = ReturnType<typeof createHoverStore>;

const SrpSalaryDotHoverContext = createContext<HoverStore | null>(null);

export function SrpSalaryDotHoverProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const storeRef = useRef<HoverStore | null>(null);
  if (!storeRef.current) storeRef.current = createHoverStore();
  return <SrpSalaryDotHoverContext.Provider value={storeRef.current}>{children}</SrpSalaryDotHoverContext.Provider>;
}

/** For a card/row: returns a stable setHovered(id | null) setter. Never triggers a re-render of the
 *  caller itself (the store doesn't feed back into it), so it's safe to call from a mouseenter/
 *  mouseleave handler on every card without any of them re-rendering on every other card's hover. */
export function useSetSrpSalaryDotHover(): (id: string | null) => void {
  const store = useContext(SrpSalaryDotHoverContext);
  return useMemo(() => (store ? store.setHovered : () => {}), [store]);
}

/** For the salary chart: the currently hovered job id, or null. Re-renders only this consumer. */
export function useSrpSalaryDotHovered(): string | null {
  const store = useContext(SrpSalaryDotHoverContext);
  return useSyncExternalStore(
    store ? store.subscribe : () => () => {},
    store ? store.getHovered : () => null,
    () => null,
  );
}
